let enableConsoleLogs = false
let logger = (...msg) => enableConsoleLogs && console.log('%cDetailsToggle:', 'color:pink;', ...msg)


/*
  An enhancement that adds a button to open/close all details
  elements within a certain container element.

  Use [data-details-toggle-root] on the details elements'
  container, and [data-details-toggle-attach] on the element
  that should contain the toggle button.


  If [data-details-toggle-attach] is a child element of
  [data-details-toggle-root], then they don't need any value
  to connect them to the same instance. For example:
  For example:

  <div data-details-toggle-root>
    <div data-details-toggle-attach> [...] </div>
    <details> [...] </details>
  </div>


  If [data-details-toggle-attach] is not a child element of
  [data-details-toggle-root], then give both of them the same
  value to connect them to the same instance. For example:

  <div data-details-toggle-attach="toc"> [...] </div>

  <ul data-details-toggle-root="toc">
    <li>
      <details> [...] </details>
    </li>
  </ul>

*/


export default class DetailsToggle {
  #openButtonContents = `<svg class="icon" height="21" viewBox="0 0 21 21" width="21" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><g fill="none" fill-rule="evenodd" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" transform="translate(7 5)"><path d="m.5 3.5 3-3 3 3"/><path d="m.5 8.5 3 3 3-3"/></g></svg> ${pgettext('details toggle button', 'Expand all')}`
  #closeButtonContents = `<svg class="icon" height="21" viewBox="0 0 21 21" width="21" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><g fill="none" fill-rule="evenodd" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" transform="translate(7 6)"><path d="m.5 9.5 3-3 3 3"/><path d="m.5.5 3 3 3-3"/></g></svg> ${pgettext('details toggle button', 'Collapse all')}`

  constructor(detailsContainerElement) {
    logger(this)

    // Look for the button container element:

    let btnContainer = detailsContainerElement.querySelector('[data-details-toggle-attach]')
    let detailsContainerId = detailsContainerElement.getAttribute('data-details-toggle-root')

    if (btnContainer) {
      this.buttonRoot = btnContainer
    }
    if (!btnContainer && detailsContainerId) {
      let detachedBtnContainer = document.querySelector(`[data-details-toggle-attach="${detailsContainerId}"]`)

      if (!detachedBtnContainer) {
        throw new Error('Can’t find a toggle button container for this DetailsToggle instance')
      }

      this.buttonRoot = detachedBtnContainer
    }
    if (!btnContainer && !detailsContainerId) {
      throw new Error('Can’t find a toggle button container for this DetailsToggle instance')
    }

    this.detailsRoot = detailsContainerElement
    this.details = this.detailsRoot.querySelectorAll('details')

    this.setupEvent = new CustomEvent('detailstoggle', {
      bubbles: true,
      detail: { isSetup: true }
    })
    this.destroyedEvent = new CustomEvent('detailstoggle', {
      bubbles: true,
      detail: { isSetup: false }
    })
    this.openedEvent = new CustomEvent('detailstoggle:opened', { bubbles: true })
    this.closingEvent = new CustomEvent('detailstoggle:closing', { bubbles: true })
    this.closedEvent = new CustomEvent('detailstoggle:closed', { bubbles: true })

    this.toggleAllDetailsCallback = this.toggleAllDetails.bind(this)
    this.updateBtnContentCallback = this.updateBtnContent.bind(this)

    this.setupToggle()
  }

  setupToggle() {
    // Add toggle button:
    this.buttonRoot.append(this.btnTemplate())
    // Register button element for event listeners etc.:
    this.toggleBtn = this.buttonRoot.querySelector('[data-details-toggle]')
    // Make sure button content reflects open/closed state of the details elements:
    this.updateBtnContent()

    // Set up event listeners:
    this.toggleBtn.addEventListener('click', this.toggleAllDetailsCallback)
    this.detailsRoot.addEventListener('detailstoggle:forceupdate', this.updateBtnContentCallback)
    this.detailsRoot.addEventListener('click', (event) => {
      if ([...this.details].includes(event.target.closest('details'))) {
        logger('heard manual details click')
        this.updateBtnContent()
      }
    })

    // Announce setup is complete:
    this.buttonRoot.dispatchEvent(this.setupEvent)
  }

  destroyToggle() {
    // Remove button from DOM:
    this.toggleBtn.remove()
    // Announce details toggle is destroyed:
    this.buttonRoot.dispatchEvent(this.destroyedEvent)
  }

  toggleAllDetails(event) {
    let areAllDetailsOpen = [...this.details].every(d => d.open === true)
    if (areAllDetailsOpen) {
      this.closeAllDetails()
    } else {
      this.openAllDetails()
    }
  }

  updateBtnContent() {
    // Use requestAnimationFrame to wait until the browser has completed
    // at least one rendering cycle, to ensure the details open states are up to date:
    requestAnimationFrame(() => {
      let areAllDetailsOpen = [...this.details].every(d => d.open === true)
      if (areAllDetailsOpen) {
        this.toggleBtn.innerHTML = this.#closeButtonContents
      } else {
        this.toggleBtn.innerHTML = this.#openButtonContents
      }
    })
  }

  openAllDetails() {
    // logger('opening all details')

    for (let detailsEl of this.details) {
      detailsEl.open = true
    }
    this.updateBtnContent()
    this.buttonRoot.dispatchEvent(this.openedEvent)
  }

  closeAllDetails() {
    // logger('closing all details')

    this.buttonRoot.dispatchEvent(this.closingEvent)

    for (let detailsEl of this.details) {
      detailsEl.open = false
    }
    this.updateBtnContent()
    this.buttonRoot.dispatchEvent(this.closedEvent)
  }

  btnTemplate() {
    let btn = document.createElement('button')
    btn.setAttribute('type', 'button')
    btn.setAttribute('data-details-toggle', '')
    btn.classList.add('btn-sm')
    btn.innerHTML = this.#openButtonContents
    return btn
  }


}
